import React, { useEffect, useState, useMemo } from 'react';
import {
  Typography,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  InputAdornment,
  Tooltip,
  Badge,
  Collapse,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Slider,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  IconButton,
  Autocomplete,
} from '@mui/material';

import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TuneIcon from '@mui/icons-material/Tune';
import HotelIcon from '@mui/icons-material/Hotel';
import VillaIcon from '@mui/icons-material/Villa';
import PeopleIcon from '@mui/icons-material/People';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import BedIcon from '@mui/icons-material/Bed';
import StarIcon from '@mui/icons-material/Star';
import CategoryIcon from '@mui/icons-material/Category';
import NumbersIcon from '@mui/icons-material/Numbers';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const ROOM_TYPES = ['STANDARD', 'SUITE', 'MASTER'];
const LUGARES = ['casaHotel', 'boutique'];
const BED_TYPES = ['Cama King Size', 'Cama Queen Size', 'Cama Doble', 'Camas Individuales', 'Sofá Cama'];

export function GestionHabitaciones({ onRoomsChange }) {
  const [rooms, setRooms] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [seeding, setSeeding] = useState(false);

  // Estados para filtros
  const [filters, setFilters] = useState({
    search: '',
    type: [],
    lugar: [],
    priceRange: [0, 5000],
    capacityRange: [1, 10],
    unitsRange: [1, 20],
    amenities: [],
    bedType: [],
    sizeRange: [0, 100],
  });

  const [showFilters, setShowFilters] = useState(false);
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);

  const emptyForm = {
    name: '',
    type: 'STANDARD',
    size: '',
    price: 0,
    totalUnits: 1,
    capacity: 1,
    bedType: '',
    amenities: [],
    description: '',
    lugar: 'casaHotel',
  };

  const [form, setForm] = useState(emptyForm);

  // Cargar habitaciones desde la API
  useEffect(() => {
    fetchRooms();
  }, []);

  useEffect(() => {
    if (onRoomsChange) onRoomsChange(rooms);
  }, [rooms, onRoomsChange]);

  // Calcular cantidad de filtros activos
  useEffect(() => {
    let count = 0;
    if (filters.search.trim()) count++;
    if (filters.type.length > 0) count++;
    if (filters.lugar.length > 0) count++;
    if (filters.priceRange[0] > 0 || filters.priceRange[1] < 5000) count++;
    if (filters.capacityRange[0] > 1 || filters.capacityRange[1] < 10) count++;
    if (filters.unitsRange[0] > 1 || filters.unitsRange[1] < 20) count++;
    if (filters.amenities.length > 0) count++;
    if (filters.bedType.length > 0) count++;
    if (filters.sizeRange[0] > 0 || filters.sizeRange[1] < 100) count++;
    setActiveFiltersCount(count);
  }, [filters]);

  async function fetchRooms() {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/rooms/admin`);
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      setRooms(data);
    } catch (err) {
      console.error('Error al cargar habitaciones:', err);
      setError('No se pudieron cargar las habitaciones. Verifica la conexión con el servidor.');
    } finally {
      setLoading(false);
    }
  }

  async function handleSeed() {
    if (!window.confirm('Esto borrará todas las habitaciones actuales y las recreará con los datos del seed. ¿Continuar?')) return;
    setSeeding(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/rooms/seed?force=true`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al ejecutar seed');
      setSuccess(`Seed completado: ${data.count} habitaciones creadas.`);
      fetchRooms();
    } catch (err) {
      setError(err.message);
    } finally {
      setSeeding(false);
    }
  }

  // Filtrar habitaciones
  const filteredRooms = useMemo(() => {
    return rooms.filter(room => {
      // Filtro de búsqueda
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        const name = (room.name || '').toLowerCase();
        const description = (room.description || '').toLowerCase();
        
        if (!name.includes(searchTerm) && !description.includes(searchTerm)) {
          return false;
        }
      }
      
      // Filtro por tipo
      if (filters.type.length > 0 && !filters.type.includes(room.type)) {
        return false;
      }
      
      // Filtro por lugar
      if (filters.lugar.length > 0 && !filters.lugar.includes(room.lugar)) {
        return false;
      }
      
      // Filtro por rango de precio
      if (room.price < filters.priceRange[0] || room.price > filters.priceRange[1]) {
        return false;
      }
      
      // Filtro por rango de capacidad
      if (room.capacity < filters.capacityRange[0] || room.capacity > filters.capacityRange[1]) {
        return false;
      }
      
      // Filtro por rango de unidades
      if (room.totalUnits < filters.unitsRange[0] || room.totalUnits > filters.unitsRange[1]) {
        return false;
      }
      
      // Filtro por amenities
      if (filters.amenities.length > 0) {
        const roomAmenities = Array.isArray(room.amenities) ? room.amenities : [];
        const hasAllAmenities = filters.amenities.every(amenity => 
          roomAmenities.some(roomAmenity => 
            roomAmenity.toLowerCase().includes(amenity.toLowerCase())
          )
        );
        if (!hasAllAmenities) return false;
      }
      
      // Filtro por tipo de cama
      if (filters.bedType.length > 0 && !filters.bedType.includes(room.bedType)) {
        return false;
      }
      
      // Filtro por tamaño (aproximado)
      if (filters.sizeRange[0] > 0 || filters.sizeRange[1] < 100) {
        // Extraer número del tamaño (si existe)
        const sizeMatch = room.size?.match(/(\d+)/);
        const roomSize = sizeMatch ? parseInt(sizeMatch[1]) : 0;
        if (roomSize < filters.sizeRange[0] || roomSize > filters.sizeRange[1]) {
          return false;
        }
      }
      
      return true;
    });
  }, [rooms, filters]);

  // Extraer todos los amenities únicos para filtros
  const allAmenities = useMemo(() => {
    const amenitiesSet = new Set();
    rooms.forEach(room => {
      if (Array.isArray(room.amenities)) {
        room.amenities.forEach(amenity => {
          if (amenity.trim()) amenitiesSet.add(amenity.trim());
        });
      }
    });
    return Array.from(amenitiesSet).sort();
  }, [rooms]);

  // Función para limpiar todos los filtros
  const clearAllFilters = () => {
    setFilters({
      search: '',
      type: [],
      lugar: [],
      priceRange: [0, 5000],
      capacityRange: [1, 10],
      unitsRange: [1, 20],
      amenities: [],
      bedType: [],
      sizeRange: [0, 100],
    });
  };

  // Estadísticas de las habitaciones
  const stats = useMemo(() => {
    const stats = {
      total: filteredRooms.length,
      casaHotel: filteredRooms.filter(r => r.lugar === 'casaHotel').length,
      boutique: filteredRooms.filter(r => r.lugar === 'boutique').length,
      totalUnits: filteredRooms.reduce((sum, r) => sum + (r.totalUnits || 0), 0),
      totalCapacity: filteredRooms.reduce((sum, r) => sum + (r.capacity || 0), 0),
      averagePrice: filteredRooms.length > 0 
        ? filteredRooms.reduce((sum, r) => sum + (r.price || 0), 0) / filteredRooms.length 
        : 0,
      types: {
        STANDARD: filteredRooms.filter(r => r.type === 'STANDARD').length,
        SUITE: filteredRooms.filter(r => r.type === 'SUITE').length,
        MASTER: filteredRooms.filter(r => r.type === 'MASTER').length,
      }
    };
    return stats;
  }, [filteredRooms]);

  function openCreate() {
    setEditing(null);
    setForm(emptyForm);
    setOpenDialog(true);
  }

  function openEdit(room) {
    setEditing(room);
    setForm({
      name: room.name || '',
      type: room.type || 'STANDARD',
      size: room.size || '',
      price: room.price || 0,
      totalUnits: room.totalUnits || 1,
      capacity: room.capacity || 1,
      bedType: room.bedType || '',
      amenities: room.amenities || [],
      description: room.description || '',
      lugar: room.lugar || 'casaHotel',
    });
    setOpenDialog(true);
  }

  async function handleDelete(id) {
    if (!window.confirm('¿Eliminar habitación? Esta acción no se puede deshacer.')) return;
    
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/rooms/admin/${id}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      setSuccess('Habitación eliminada exitosamente');
      await fetchRooms();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error al eliminar habitación:', err);
      setError('No se pudo eliminar la habitación. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmitForm(e) {
    e.preventDefault();
    if (!form.name) {
      setError('El nombre es requerido');
      return;
    }

    const roomData = {
      name: form.name,
      type: form.type,
      description: form.description,
      price: Number(form.price),
      size: form.size,
      capacity: Number(form.capacity),
      bedType: form.bedType,
      amenities: Array.isArray(form.amenities) ? form.amenities : [],
      lugar: form.lugar,
      totalUnits: Number(form.totalUnits)
    };

    setLoading(true);
    setError(null);
    
    try {
      let response;
      
      if (editing) {
        response = await fetch(`${API_URL}/api/rooms/admin/${editing._id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(roomData),
        });
      } else {
        response = await fetch(`${API_URL}/api/rooms/admin`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(roomData),
        });
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Error ${response.status}`);
      }

      setSuccess(editing ? 'Habitación actualizada exitosamente' : 'Habitación creada exitosamente');
      setOpenDialog(false);
      await fetchRooms();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error al guardar habitación:', err);
      setError(err.message || 'No se pudo guardar la habitación. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  }

  function handleChangeField(key, value) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  function handleChangeAmenities(e) {
    const value = e.target.value;
    const amenitiesArray = value.split(',').map(a => a.trim()).filter(a => a);
    handleChangeField('amenities', amenitiesArray);
  }

  // Componente de filtros avanzados
  const AdvancedFilters = () => (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardHeader
        avatar={
          <Avatar sx={{ bgcolor: 'primary.light' }}>
            <TuneIcon />
          </Avatar>
        }
        title={
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Filtros Avanzados</Typography>
            <Badge badgeContent={activeFiltersCount} color="primary">
              <FilterAltIcon color="action" />
            </Badge>
          </Stack>
        }
        subheader="Filtra habitaciones por características específicas"
        action={
          <Stack direction="row" spacing={1}>
            <IconButton 
              size="small" 
              onClick={clearAllFilters}
              disabled={activeFiltersCount === 0}
            >
              <FilterAltOffIcon />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Stack>
        }
      />
      <Collapse in={showFilters}>
        <CardContent>
          <Grid container spacing={3}>
            {/* Rango de precios */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Precio por noche: ${filters.priceRange[0]} - ${filters.priceRange[1]}
              </Typography>
              <Slider
                value={filters.priceRange}
                onChange={(_, newValue) => setFilters(prev => ({
                  ...prev,
                  priceRange: newValue
                }))}
                valueLabelDisplay="auto"
                min={0}
                max={5000}
                step={100}
                valueLabelFormat={(value) => `$${value}`}
                marks={[
                  { value: 0, label: '$0' },
                  { value: 1000, label: '$1,000' },
                  { value: 2000, label: '$2,000' },
                  { value: 3000, label: '$3,000' },
                  { value: 4000, label: '$4,000' },
                  { value: 5000, label: '$5,000' },
                ]}
              />
            </Grid>

            {/* Rango de capacidad */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Capacidad: {filters.capacityRange[0]} - {filters.capacityRange[1]} personas
              </Typography>
              <Slider
                value={filters.capacityRange}
                onChange={(_, newValue) => setFilters(prev => ({
                  ...prev,
                  capacityRange: newValue
                }))}
                valueLabelDisplay="auto"
                min={1}
                max={10}
                step={1}
                marks={[
                  { value: 1, label: '1' },
                  { value: 2, label: '2' },
                  { value: 4, label: '4' },
                  { value: 6, label: '6' },
                  { value: 8, label: '8' },
                  { value: 10, label: '10' },
                ]}
              />
            </Grid>

            {/* Rango de unidades */}
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom>
                Unidades: {filters.unitsRange[0]} - {filters.unitsRange[1]}
              </Typography>
              <Slider
                value={filters.unitsRange}
                onChange={(_, newValue) => setFilters(prev => ({
                  ...prev,
                  unitsRange: newValue
                }))}
                valueLabelDisplay="auto"
                min={1}
                max={20}
                step={1}
                marks={[
                  { value: 1, label: '1' },
                  { value: 5, label: '5' },
                  { value: 10, label: '10' },
                  { value: 15, label: '15' },
                  { value: 20, label: '20' },
                ]}
              />
            </Grid>

            {/* Rango de tamaño aproximado */}
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom>
                Tamaño: {filters.sizeRange[0]} - {filters.sizeRange[1]} m²
              </Typography>
              <Slider
                value={filters.sizeRange}
                onChange={(_, newValue) => setFilters(prev => ({
                  ...prev,
                  sizeRange: newValue
                }))}
                valueLabelDisplay="auto"
                min={0}
                max={100}
                step={5}
                marks={[
                  { value: 0, label: '0' },
                  { value: 25, label: '25' },
                  { value: 50, label: '50' },
                  { value: 75, label: '75' },
                  { value: 100, label: '100' },
                ]}
              />
            </Grid>

            {/* Filtro por tipo */}
            <Grid item xs={12} md={4}>
              <FormControl component="fieldset" fullWidth>
                <Typography variant="subtitle2" gutterBottom>
                  Tipo de habitación
                </Typography>
                <FormGroup>
                  {ROOM_TYPES.map((type) => (
                    <FormControlLabel
                      key={type}
                      control={
                        <Checkbox
                          size="small"
                          checked={filters.type.includes(type)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFilters(prev => ({
                                ...prev,
                                type: [...prev.type, type]
                              }));
                            } else {
                              setFilters(prev => ({
                                ...prev,
                                type: prev.type.filter(t => t !== type)
                              }));
                            }
                          }}
                        />
                      }
                      label={
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <CategoryIcon fontSize="small" />
                          <Typography variant="body2">{type}</Typography>
                        </Stack>
                      }
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </Grid>

            {/* Filtro por amenities */}
            <Grid item xs={12} md={6}>
              <FormControl component="fieldset" fullWidth>
                <Typography variant="subtitle2" gutterBottom>
                  Servicios incluidos
                </Typography>
                <FormGroup row sx={{ flexWrap: 'wrap' }}>
                  {allAmenities.slice(0, 8).map((amenity) => (
                    <FormControlLabel
                      key={amenity}
                      control={
                        <Checkbox
                          size="small"
                          checked={filters.amenities.includes(amenity)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFilters(prev => ({
                                ...prev,
                                amenities: [...prev.amenities, amenity]
                              }));
                            } else {
                              setFilters(prev => ({
                                ...prev,
                                amenities: prev.amenities.filter(a => a !== amenity)
                              }));
                            }
                          }}
                        />
                      }
                      label={
                        <Typography variant="caption">
                          {amenity}
                        </Typography>
                      }
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </Grid>

            {/* Filtro por tipo de cama */}
            <Grid item xs={12} md={6}>
              <FormControl component="fieldset" fullWidth>
                <Typography variant="subtitle2" gutterBottom>
                  Tipo de cama
                </Typography>
                <FormGroup row sx={{ flexWrap: 'wrap' }}>
                  {BED_TYPES.slice(0, 5).map((bedType) => (
                    <FormControlLabel
                      key={bedType}
                      control={
                        <Checkbox
                          size="small"
                          checked={filters.bedType.includes(bedType)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFilters(prev => ({
                                ...prev,
                                bedType: [...prev.bedType, bedType]
                              }));
                            } else {
                              setFilters(prev => ({
                                ...prev,
                                bedType: prev.bedType.filter(b => b !== bedType)
                              }));
                            }
                          }}
                        />
                      }
                      label={
                        <Typography variant="caption">
                          {bedType}
                        </Typography>
                      }
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Collapse>
    </Card>
  );

  // Chips de filtros activos
  const ActiveFilterChips = () => (
    <Stack 
      direction="row" 
      spacing={1} 
      sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}
      alignItems="center"
    >
      <Typography variant="caption" color="textSecondary">
        Filtros activos:
      </Typography>
      
      {/* Search filter */}
      {filters.search && (
        <Chip
          label={`Buscar: "${filters.search}"`}
          size="small"
          onDelete={() => setFilters(prev => ({ ...prev, search: '' }))}
          deleteIcon={<ClearIcon />}
        />
      )}
      
      {/* Type filters */}
      {filters.type.map(type => (
        <Chip
          key={type}
          label={`Tipo: ${type}`}
          size="small"
          color="primary"
          onDelete={() => setFilters(prev => ({
            ...prev,
            type: prev.type.filter(t => t !== type)
          }))}
          deleteIcon={<ClearIcon />}
        />
      ))}
      
      {/* Lugar filters */}
      {filters.lugar.map(lugar => (
        <Chip
          key={lugar}
          label={`Lugar: ${lugar === 'casaHotel' ? 'Casa Hotel' : 'Boutique'}`}
          size="small"
          color="info"
          onDelete={() => setFilters(prev => ({
            ...prev,
            lugar: prev.lugar.filter(l => l !== lugar)
          }))}
          deleteIcon={<ClearIcon />}
        />
      ))}
      
      {/* Price range filter */}
      {(filters.priceRange[0] > 0 || filters.priceRange[1] < 5000) && (
        <Chip
          label={`Precio: $${filters.priceRange[0]} - $${filters.priceRange[1]}`}
          size="small"
          color="warning"
          onDelete={() => setFilters(prev => ({
            ...prev,
            priceRange: [0, 5000]
          }))}
          deleteIcon={<ClearIcon />}
        />
      )}
      
      {/* Capacity range filter */}
      {(filters.capacityRange[0] > 1 || filters.capacityRange[1] < 10) && (
        <Chip
          label={`Capacidad: ${filters.capacityRange[0]} - ${filters.capacityRange[1]} pers.`}
          size="small"
          color="success"
          onDelete={() => setFilters(prev => ({
            ...prev,
            capacityRange: [1, 10]
          }))}
          deleteIcon={<ClearIcon />}
        />
      )}
      
      {/* Clear all button */}
      {activeFiltersCount > 0 && (
        <Button
          size="small"
          startIcon={<ClearIcon />}
          onClick={clearAllFilters}
          sx={{ ml: 1 }}
        >
          Limpiar todos
        </Button>
      )}
    </Stack>
  );

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <MeetingRoomIcon />
          <Typography variant="h6">Gestión de habitaciones</Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            color="warning"
            startIcon={seeding ? <CircularProgress size={18} /> : <RestartAltIcon />}
            onClick={handleSeed}
            disabled={seeding || loading}
          >
            {seeding ? 'Sembrando...' : 'Reseed Habitaciones'}
          </Button>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />} 
            onClick={openCreate}
            disabled={loading}
          >
            Nueva habitación
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Barra de búsqueda principal */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={2}>
            <TextField
              fullWidth
              placeholder="Buscar por nombre o descripción..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: filters.search && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setFilters(prev => ({ ...prev, search: '' }))}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              variant="outlined"
              size="small"
            />

            {/* Filtros rápidos por tipo */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Tipo de habitación:
              </Typography>
              <ToggleButtonGroup
                value={filters.type}
                onChange={(_, newTypes) => setFilters(prev => ({ ...prev, type: newTypes }))}
                aria-label="type filters"
                size="small"
                sx={{ flexWrap: 'wrap', gap: 1 }}
              >
                {ROOM_TYPES.map((type) => (
                  <ToggleButton
                    key={type}
                    value={type}
                    selected={filters.type.includes(type)}
                    sx={{
                      borderColor: 'primary.light',
                      '&.Mui-selected': {
                        backgroundColor: 'primary.main',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'primary.dark',
                        },
                      },
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <CategoryIcon fontSize="small" />
                      <Typography variant="body2">{type}</Typography>
                    </Stack>
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            {/* Filtros rápidos por lugar */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Lugar:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                <Chip
                  label="Casa Hotel"
                  clickable
                  color={filters.lugar.includes('casaHotel') ? 'primary' : 'default'}
                  variant={filters.lugar.includes('casaHotel') ? 'filled' : 'outlined'}
                  onClick={() => {
                    if (filters.lugar.includes('casaHotel')) {
                      setFilters(prev => ({
                        ...prev,
                        lugar: prev.lugar.filter(l => l !== 'casaHotel')
                      }));
                    } else {
                      setFilters(prev => ({
                        ...prev,
                        lugar: [...prev.lugar, 'casaHotel']
                      }));
                    }
                  }}
                  onDelete={
                    filters.lugar.includes('casaHotel')
                      ? () => setFilters(prev => ({
                          ...prev,
                          lugar: prev.lugar.filter(l => l !== 'casaHotel')
                        }))
                      : undefined
                  }
                  deleteIcon={<ClearIcon />}
                  icon={<HotelIcon fontSize="small" />}
                />
                <Chip
                  label="Boutique"
                  clickable
                  color={filters.lugar.includes('boutique') ? 'secondary' : 'default'}
                  variant={filters.lugar.includes('boutique') ? 'filled' : 'outlined'}
                  onClick={() => {
                    if (filters.lugar.includes('boutique')) {
                      setFilters(prev => ({
                        ...prev,
                        lugar: prev.lugar.filter(l => l !== 'boutique')
                      }));
                    } else {
                      setFilters(prev => ({
                        ...prev,
                        lugar: [...prev.lugar, 'boutique']
                      }));
                    }
                  }}
                  onDelete={
                    filters.lugar.includes('boutique')
                      ? () => setFilters(prev => ({
                          ...prev,
                          lugar: prev.lugar.filter(l => l !== 'boutique')
                        }))
                      : undefined
                  }
                  deleteIcon={<ClearIcon />}
                  icon={<VillaIcon fontSize="small" />}
                />
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Filtros activos */}
      {activeFiltersCount > 0 && <ActiveFilterChips />}

      {/* Filtros avanzados */}
      <AdvancedFilters />

      {/* Estadísticas */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center', p: 2 }}>
              <Typography variant="h4" color="primary">
                {stats.total}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Habitaciones
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center', p: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
                <HotelIcon fontSize="small" color="primary" />
                <Typography variant="h4">{stats.casaHotel}</Typography>
              </Stack>
              <Typography variant="caption" color="textSecondary">
                Casa Hotel
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center', p: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
                <VillaIcon fontSize="small" color="secondary" />
                <Typography variant="h4">{stats.boutique}</Typography>
              </Stack>
              <Typography variant="caption" color="textSecondary">
                Boutique
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center', p: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
                <NumbersIcon fontSize="small" color="info" />
                <Typography variant="h4">{stats.totalUnits}</Typography>
              </Stack>
              <Typography variant="caption" color="textSecondary">
                Unidades totales
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center', p: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
                <PeopleIcon fontSize="small" color="success" />
                <Typography variant="h4">{stats.totalCapacity}</Typography>
              </Stack>
              <Typography variant="caption" color="textSecondary">
                Capacidad total
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center', p: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
                <AttachMoneyIcon fontSize="small" color="warning" />
                <Typography variant="h4">${Math.round(stats.averagePrice)}</Typography>
              </Stack>
              <Typography variant="caption" color="textSecondary">
                Precio promedio
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tipos de habitación */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Distribución por tipo:
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
          <Chip 
            label={`STANDARD: ${stats.types.STANDARD}`} 
            color="primary" 
            variant="outlined" 
            size="small"
          />
          <Chip 
            label={`SUITE: ${stats.types.SUITE}`} 
            color="secondary" 
            variant="outlined" 
            size="small"
          />
          <Chip 
            label={`MASTER: ${stats.types.MASTER}`} 
            color="success" 
            variant="outlined" 
            size="small"
          />
        </Stack>
      </Box>

      {/* Contador de resultados */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="body2" color="textSecondary">
          Mostrando {filteredRooms.length} de {rooms.length} habitaciones
          {activeFiltersCount > 0 && ` (filtradas)`}
        </Typography>
        {activeFiltersCount > 0 && (
          <Button 
            startIcon={<FilterAltOffIcon />} 
            size="small" 
            onClick={clearAllFilters}
            variant="text"
          >
            Limpiar filtros
          </Button>
        )}
      </Box>

      {loading && !openDialog ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Precio</TableCell>
                <TableCell>Unidades</TableCell>
                <TableCell>Lugar</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRooms.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Stack spacing={2} alignItems="center">
                      <FilterListIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                      <Typography variant="body1" color="text.secondary">
                        No se encontraron habitaciones
                      </Typography>
                      {activeFiltersCount > 0 && (
                        <Button 
                          variant="outlined" 
                          onClick={clearAllFilters}
                          startIcon={<ClearIcon />}
                        >
                          Limpiar filtros
                        </Button>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRooms.map(r => (
                  <TableRow key={r._id} hover>
                    <TableCell>
                      <Box>
                        <Typography sx={{ fontWeight: 700 }}>{r.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {r.description?.substring(0, 60)}{r.description?.length > 60 ? '...' : ''}
                        </Typography>
                        <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                          <Chip 
                            label={`${r.capacity} pers.`} 
                            size="small" 
                            variant="outlined"
                            icon={<PeopleIcon fontSize="small" />}
                          />
                          <Chip 
                            label={r.size || 'N/A'} 
                            size="small" 
                            variant="outlined"
                            icon={<SquareFootIcon fontSize="small" />}
                          />
                          {r.bedType && (
                            <Chip 
                              label={r.bedType} 
                              size="small" 
                              variant="outlined"
                              icon={<BedIcon fontSize="small" />}
                            />
                          )}
                        </Stack>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={r.type} 
                        size="small" 
                        color={
                          r.type === 'STANDARD' ? 'primary' : 
                          r.type === 'SUITE' ? 'secondary' : 'success'
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <AttachMoneyIcon sx={{ fontSize: 18, color: 'success.main' }} />
                        <Typography fontWeight="bold" color="success.main">
                          {Number(r.price).toFixed(2)}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Inventory2Icon sx={{ fontSize: 18, color: 'info.main' }} />
                        <Typography fontWeight="bold" color="info.main">
                          {r.totalUnits}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={r.lugar === 'casaHotel' ? 'Casa Hotel' : 'Boutique'} 
                        size="small"
                        color={r.lugar === 'casaHotel' ? 'primary' : 'secondary'}
                        variant="outlined"
                        icon={r.lugar === 'casaHotel' ? <HotelIcon fontSize="small" /> : <VillaIcon fontSize="small" />}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Editar habitación">
                          <IconButton 
                            size="small" 
                            onClick={() => openEdit(r)}
                            disabled={loading}
                            color="primary"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar habitación">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleDelete(r._id)}
                            disabled={loading}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Editar habitación' : 'Crear habitación'}</DialogTitle>
        <Box component="form" onSubmit={handleSubmitForm}>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField 
              label="Nombre" 
              value={form.name} 
              onChange={e => handleChangeField('name', e.target.value)} 
              required 
              disabled={loading}
            />
            
            <Stack direction="row" spacing={2}>
              <FormControl fullWidth>
                <InputLabel id="tipo-label">Tipo</InputLabel>
                <Select 
                  labelId="tipo-label" 
                  value={form.type || 'STANDARD'} 
                  label="Tipo" 
                  onChange={e => handleChangeField('type', e.target.value)}
                  disabled={loading}
                >
                  {ROOM_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel id="lugar-label">Lugar</InputLabel>
                <Select 
                  labelId="lugar-label" 
                  value={form.lugar} 
                  label="Lugar" 
                  onChange={e => handleChangeField('lugar', e.target.value)}
                  disabled={loading}
                >
                  {LUGARES.map(l => (
                    <MenuItem key={l} value={l}>
                      {l === 'casaHotel' ? 'Casa Hotel' : 'Boutique'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            
            <TextField 
              label="Descripción" 
              value={form.description} 
              onChange={e => handleChangeField('description', e.target.value)} 
              multiline 
              rows={3} 
              disabled={loading}
            />

            <Stack direction="row" spacing={2}>
              <TextField 
                label="Precio por noche (MXN)" 
                type="number" 
                value={form.price} 
                onChange={e => handleChangeField('price', Number(e.target.value))} 
                fullWidth
                disabled={loading}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
              <TextField 
                label="Unidades totales" 
                type="number" 
                inputProps={{ min: 1 }} 
                value={form.totalUnits} 
                onChange={e => handleChangeField('totalUnits', Math.max(1, Number(e.target.value)))} 
                fullWidth
                disabled={loading}
              />
            </Stack>

            <Stack direction="row" spacing={2}>
              <TextField 
                label="Capacidad (personas)" 
                type="number" 
                inputProps={{ min: 1 }} 
                value={form.capacity} 
                onChange={e => handleChangeField('capacity', Math.max(1, Number(e.target.value)))} 
                fullWidth
                disabled={loading}
              />
              <TextField 
                label="Tamaño" 
                value={form.size} 
                onChange={e => handleChangeField('size', e.target.value)} 
                placeholder="25 m²"
                fullWidth
                disabled={loading}
              />
            </Stack>

            <FormControl fullWidth>
              <InputLabel id="bed-type-label">Tipo de cama</InputLabel>
              <Select 
                labelId="bed-type-label" 
                value={form.bedType} 
                label="Tipo de cama" 
                onChange={e => handleChangeField('bedType', e.target.value)}
                disabled={loading}
              >
                <MenuItem value="">
                  <em>Seleccionar tipo de cama</em>
                </MenuItem>
                {BED_TYPES.map(bed => (
                  <MenuItem key={bed} value={bed}>{bed}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField 
              label="Servicios incluidos (separados por comas)" 
              value={Array.isArray(form.amenities) ? form.amenities.join(', ') : ''} 
              onChange={handleChangeAmenities}
              placeholder="WiFi, Aire acondicionado, Smart TV"
              helperText="Separa cada servicio con una coma"
              disabled={loading}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)} disabled={loading}>
              Cancelar
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Paper>
  );
}